import Spinner from '@ds/components/feedback/Spinner';
import clsx from 'clsx';
import React, { type AnchorHTMLAttributes, type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react';

import styles from './Button.module.css';

/** Visual priority of the button. Controls background, border, and text colour. */
export type ButtonVariant = 'primary' | 'secondary' | 'text' | 'link' | 'elevatedPrimary' | 'elevatedSecondary';

/** Size scale. Controls height and label typography tokens. */
export type ButtonSize = 'sm' | 'lg';

export interface ButtonProps
    extends Omit<ButtonHTMLAttributes<HTMLButtonElement> & AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
    /**
     * Visual priority of the action.
     * - `primary` — filled, high emphasis. Use sparingly; one per decision area.
     * - `secondary` — outlined, medium emphasis. Carries **both** a `--bg-surface` fill and an
     *   `--action-primary` outline; "outlined" names the border, not an absent fill. Use for
     *   alternatives alongside a primary action. Pair with `transparent` on a coloured surface.
     * - `text` — ghost, low emphasis. Use for supporting or toolbar actions.
     * - `link` — reads as inline text: semibold, with a 2px underline in every state (disabled
     *   included). Use for actions (not navigation) that sit within body copy.
     *   For navigation, use `<Link>` instead.
     * - `elevatedSecondary` — `--bg-surface-2` surface, `--action-primary` ink, lifted on a shadow. Use for a
     *   control that floats *over* other content rather than sitting in the flow with it (Figma
     *   "Small FAB"). This is the direct successor of the old single `elevated` variant.
     * - `elevatedPrimary` — solid fill on `--action-link`, `--text-on-accent` ink, same shadow as
     *   `elevatedSecondary`. For an elevated control that needs to read as the primary action.
     *
     * Neither elevated variant supports `danger` — a deliberate rule, not a gap: `danger` is a
     * silent no-op on both, always rendering their normal colours.
     * @default 'primary'
     */
    variant?: ButtonVariant;
    /**
     * Size scale. Match to the density of the surrounding UI.
     * - `sm` (40px) — dense controls, tables, toolbars.
     * - `lg` (56px) — high-emphasis calls to action, larger touch targets.
     * @default 'sm'
     */
    size?: ButtonSize;
    /**
     * Applies danger colour treatment. Composes with `variant` to preserve action priority.
     * Prefer `secondary danger` unless the destructive action is the primary expected action.
     * A silent no-op on `elevatedPrimary`/`elevatedSecondary` — neither elevated variant supports
     * `danger` by design.
     * @default false
     */
    danger?: boolean;
    /**
     * Drops the resting fill so whatever the button sits on shows through, keeping the outline and
     * the ink. Reach for it on a coloured surface — a tinted action band, a tinted panel — where an
     * opaque white pill would read as a foreign object.
     *
     * **`secondary` only, and silently ignored elsewhere.** `text` and `link` are already
     * transparent, and `elevated`'s surface is the whole variant. It composes with `danger`.
     *
     * Only the **resting** and **disabled** fills change: hover still fills
     * `--action-secondary-hover` and pressed/focus still fill `--action-secondary-pressed`, so the
     * hover affordance survives. This is the appearance `secondary` had before it gained a surface.
     *
     * Not a substitute for `variant="text"`, which has no outline at all. See ADR 0038.
     * @default false
     */
    transparent?: boolean;
    /**
     * Shows a loading spinner, hides icons, and blocks interaction.
     * Sets `aria-busy="true"` and **soft**-disables (`aria-disabled="true"`, `onClick` withheld)
     * rather than applying the native `disabled` attribute, so a button that starts loading while
     * focused keeps focus instead of dropping it to `<body>`. See ADR 0051.
     * Update `children` to reflect the in-progress state (e.g. "Saving…").
     * @default false
     */
    isLoading?: boolean;
    /** Icon rendered before the label. Treated as decorative (`aria-hidden`). Hidden when `isLoading`. */
    startIcon?: ReactNode;
    /** Icon rendered after the label. Treated as decorative (`aria-hidden`). Hidden when `isLoading`. */
    endIcon?: ReactNode;
    /**
     * Stretches the button to fill its container width.
     * @default false
     */
    fullWidth?: boolean;
    /**
     * Renders the label exactly as written. By default the first letter of the label is
     * capitalised in CSS (`::first-letter`), so copy assembled from data reads as sentence case.
     * Set this when the first letter is lower case on purpose — a product name such as
     * "eNVD Profile", "eDECs" or "myMLA" — and the capital would be wrong.
     * @default false
     */
    preserveCase?: boolean;
    /**
     * Changes the rendered HTML element or component.
     * Use `as="a"` with `href` for link-styled buttons, or `as={RouterLink}` for client-side navigation.
     * When not `'button'`, the native `disabled` attribute and `type` are not forwarded.
     * @default 'button'
     */
    as?: React.ElementType;
    /** Forwarded to the root element when `as` is a non-button element (e.g. `as="a"`). */
    href?: string;
}

/**
 * Primary interactive primitive for user-initiated actions.
 *
 * Use `Button` for actions (save, delete, submit, open modal).
 * Use `<Link>` for navigation (routes to a page via `href`).
 * Use `<Button variant="link">` for actions that read visually as links but have no destination URL.
 *
 * All visual state is reflected as `data-*` attributes on the root element,
 * making the active variant visible in DevTools and stable for CSS/test selectors.
 *
 * @example
 * // Basic usage
 * <Button onClick={handleSave}>Save consignment</Button>
 *
 * @example
 * // Danger action — prefer secondary for most destructive actions
 * <Button variant="secondary" danger onClick={handleDelete}>
 *     Delete device
 * </Button>
 *
 * @example
 * // Polymorphic — render as an anchor for link-level prominence
 * <Button as="a" href="/consignments" variant="primary">
 *     View consignments
 * </Button>
 */
const Button = forwardRef<HTMLElement, ButtonProps>(
    (
        {
            as: component,
            variant = 'primary',
            size = 'sm',
            danger = false,
            transparent = false,
            isLoading = false,
            startIcon,
            endIcon,
            fullWidth = false,
            preserveCase = false,
            disabled,
            className,
            children,
            type = 'button',
            href,
            onClick,
            'aria-disabled': ariaDisabled,
            ...rest
        },
        ref
    ) => {
        const Tag = component ?? 'button';
        const isButton = Tag === 'button';

        return (
            <Tag
                ref={ref}
                {...(isButton ? { type, disabled } : { href })}
                aria-busy={isLoading || undefined}
                // Loading is a soft disable: the native attribute would drop focus to <body> the
                // instant a focused button starts loading. `aria-disabled` keeps it focusable and
                // announced; Button.module.css gives it the disabled fill and `pointer-events: none`,
                // and withholding onClick closes Enter and Space. A caller's own aria-disabled wins
                // when not loading. See ADR 0051.
                aria-disabled={isLoading ? true : ariaDisabled}
                onClick={isLoading ? undefined : onClick}
                data-variant={variant}
                data-size={size}
                data-danger={danger || undefined}
                data-transparent={transparent || undefined}
                data-loading={isLoading || undefined}
                data-full-width={fullWidth || undefined}
                data-preserve-case={preserveCase || undefined}
                data-start-icon={startIcon ? true : undefined}
                data-end-icon={endIcon ? true : undefined}
                className={clsx(styles.button, className)}
                {...rest}
            >
                {isLoading && (
                    <Spinner className={styles.spinner} />
                )}
                {!isLoading && startIcon && (
                    <span
                        className={styles.icon}
                        aria-hidden="true"
                    >
                        {startIcon}
                    </span>
                )}
                <span className={styles.label}>{children}</span>
                {!isLoading && endIcon && (
                    <span
                        className={styles.icon}
                        aria-hidden="true"
                    >
                        {endIcon}
                    </span>
                )}
            </Tag>
        );
    }
);

Button.displayName = 'Button';

export { Button };
export default Button;
